import Error from '@mui/icons-material/Error';
import PersonIcon from '@mui/icons-material/Person';
import {
  Alert,
  AlertColor,
  Avatar,
  Box,
  Button,
  CircularProgress,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Skeleton,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { CSSProperties, FC, ReactNode, useState } from 'react';

import { Piece, RecipientAutoDetection, RecipientAutoDetectionStatus, User, UserMin } from '@apiContract';
import { RecipientMessages, recipientAlerts } from '@components/Capture/helper';
import { sxSearchCircle } from '@components/Capture/styles';
import { Iconify } from '@components/Iconify';
import { ClickableBox } from '@uiKit/ClickableBox';
import { m } from 'framer-motion';
import { useUserInfoLines } from 'src/hooks/useUserInfoLines';
import { customScrollbar } from 'src/theme/shared/scrollbar';

import { sxAssignButton, sxAssignToBox, sxAvatarBigSize, sxIconGrey500 } from '../styles';

type AssignToProps = {
  recipientDetection?: RecipientAutoDetection;
  user?: User | UserMin | null;
  showAlert?: boolean;
  loading?: boolean;
  piece?: Piece | null;
  applyAutoDetection?: () => void;
  rightAdornment?: ReactNode;
  unassignedText?: string;
  style?: CSSProperties;
  onClick?: () => void;
};

const getAlert = (
  showAlert: boolean,
  recipientDetection?: RecipientAutoDetection,
  piece?: Piece | null,
  prevDetect?: RecipientAutoDetection,
  prevRecipient?: UserMin,
) => {
  // https://digitalizedsoftware.atlassian.net/wiki/spaces/RD/pages/50200577/Recipient+Auto+Assign+Logic#Assign-drawer-alerts

  if (!showAlert) return null;

  const assignId = piece?.recipient?.id;
  const status = piece?.recipientAutoDetection?.status || recipientDetection?.status;

  switch (true) {
    case (prevDetect?.status !== status && status !== RecipientAutoDetectionStatus.Failed) ||
      prevRecipient?.id !== assignId:
      return recipientAlerts[RecipientMessages.refresh];

    case status === RecipientAutoDetectionStatus.Identified && assignId === recipientDetection?.recipientId:
      return recipientAlerts[RecipientMessages.identifiedSuccessfully];

    case status === RecipientAutoDetectionStatus.IdentifiedBasedOnPreviousAssignment &&
      assignId === recipientDetection?.recipientId:
      return recipientAlerts[RecipientMessages.previousAssignment];

    case status === RecipientAutoDetectionStatus.IdentifiedVerificationRequired && !assignId:
      return recipientAlerts[RecipientMessages.requiresValidation];

    case status === RecipientAutoDetectionStatus.IdentifiedMultipleMatches && !assignId:
      return recipientAlerts[RecipientMessages.possibleMatches];

    case status === RecipientAutoDetectionStatus.IdentifiedAndNotExist && !assignId:
      return recipientAlerts[RecipientMessages.identifiedNotExist];

    case status === RecipientAutoDetectionStatus.Failed && prevDetect?.status === status && !assignId:
      return recipientAlerts[RecipientMessages.unableToIdentify];

    case (status === RecipientAutoDetectionStatus.Identified ||
      status === RecipientAutoDetectionStatus.IdentifiedBasedOnPreviousAssignment) &&
      !assignId:
      return recipientAlerts[RecipientMessages.identifiedNotAssigned];

    default:
      return null;
  }
};

type LineComponentProps = {
  icon: string;
  line?: string | null;
  sx?: SxProps<Theme>;
};

const LineComponent = ({ icon, line, sx = {} }: LineComponentProps) => {
  const theme = useTheme();

  if (!line) return;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', textWrap: 'nowrap', ...sx }}>
      <Iconify icon={icon} width={16} height={16} color={theme.palette.text.secondary} sx={{ m: '0 4px 0 0' }} />
      <Typography variant="caption" sx={{ color: 'grey.500' }}>
        {line}
      </Typography>
    </Box>
  );
};
const AssignTO: FC<AssignToProps> = ({
  applyAutoDetection,
  piece,
  user,
  showAlert = false,
  recipientDetection,
  loading = false,
  rightAdornment,
  unassignedText,
  style = {},
  onClick,
}) => {
  const [initPieceRecipient, setInitPieceRecipient] = useState<UserMin | undefined>(piece?.recipient);
  const [initPieceDetection, setInitPieceDetection] = useState<RecipientAutoDetection | undefined>(
    piece?.recipientAutoDetection || recipientDetection,
  );
  const theme = useTheme();

  const {
    idLine,
    routesLine,
    isRoutesLoading,
    userGroups,
    isLoadingGroups,
    showGrowps,
    isShowLockerLine,
    lockerLine,
    isLoadingLocker,
  } = useUserInfoLines(user, true);

  const alert = getAlert(showAlert, recipientDetection, piece, initPieceDetection, initPieceRecipient);

  const onRefreshClick = () => {
    setInitPieceRecipient(piece?.recipient);
    setInitPieceDetection(piece?.recipientAutoDetection || recipientDetection);
    if (applyAutoDetection) applyAutoDetection();
  };

  const Container = onClick ? ClickableBox : Box;
  const containerProps = onClick ? { onClick } : {};

  return (
    <Container sx={{ ...sxAssignToBox, ...style }} {...containerProps}>
      {alert && (
        <m.div
          transition={{
            duration: 2,
            ease: 'easeInOut',
            repeatDelay: 1,
            repeat: Infinity,
          }}
        >
          <Alert
            icon={alert.type === 'error' ? <Error /> : undefined}
            action={
              alert.type === 'info' ? (
                <Button color="info" variant="contained" onClick={onRefreshClick}>
                  Refresh
                </Button>
              ) : undefined
            }
            severity={alert.type as AlertColor}
          >
            {alert.text}
          </Alert>
        </m.div>
      )}
      <ListItem disablePadding secondaryAction={!!rightAdornment && user && rightAdornment} component="div">
        <Tooltip title={loading ? 'Identifying recipient' : ''}>
          <ListItemButton sx={{ ...sxAssignButton(theme), ...customScrollbar(theme) }}>
            <Box
              display="flex"
              flexDirection="row"
              alignItems={routesLine ? 'top' : 'center'}
              flex="1"
              className="customScrollBar"
              sx={{
                width: '100%',
                overflow: 'auto',
                pb: 1.5,
                mb: 0.5,
              }}
            >
              <ListItemAvatar>
                <Avatar sx={sxAvatarBigSize} alt={`${user?.displayName}`} src={user?.profileImageUrl}>
                  {user && !user?.profileImageUrl ? (
                    `${user?.firstName?.charAt(0)}${user?.lastName?.charAt(0)}`
                  ) : (
                    <PersonIcon sx={sxIconGrey500} />
                  )}
                </Avatar>
              </ListItemAvatar>
              {user ? (
                <ListItemText
                  primary={
                    <Typography
                      title={`${user.firstName} ${user.lastName}`}
                      variant="subtitle1"
                    >{`${user.firstName} ${user.lastName}`}</Typography>
                  }
                  secondary={
                    <>
                      <Typography
                        title={user.userName}
                        color="text.secondary"
                        variant="body1"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        data-testid="rd-assign-to-username"
                      >
                        {idLine}
                      </Typography>
                      {!!user.groupIds?.length &&
                        showGrowps &&
                        (isLoadingGroups ? (
                          <Skeleton variant="text" width="40%" />
                        ) : (
                          <LineComponent icon={'material-symbols:groups'} line={userGroups?.sort().join(', ')} />
                        ))}
                      {routesLine &&
                        (isRoutesLoading ? (
                          <Skeleton variant="text" width="40%" />
                        ) : (
                          <LineComponent sx={{ mt: 0.5 }} icon={'material-symbols:mode-of-travel'} line={routesLine} />
                        ))}
                      {isShowLockerLine &&
                        (isLoadingLocker ? (
                          <Skeleton variant="text" width="40%" />
                        ) : (
                          <LineComponent sx={{ mt: 0.5 }} icon={'material-symbols:grid-on'} line={lockerLine} />
                        ))}
                    </>
                  }
                />
              ) : (
                <Typography variant="subtitle2">{unassignedText || 'Unassigned user'}</Typography>
              )}
            </Box>
            {loading && (
              <Box display="flex" justifyContent="center">
                <CircularProgress size={22} sx={sxSearchCircle} />
              </Box>
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>
    </Container>
  );
};

export default AssignTO;
